import React from 'react';

import type {AutoDeliveryFrequency} from '@/consts/icc/iccAutoDeliveryStatusTypes';
import appContent from '@/services/content/appContent.typed';
import type {AutoDeliveryBase} from '@/types/icc/autoDeliveryDetails';
import stringUtils from '@/utils/stringUtils';
import {twx} from '@/utils/tailwindUtils';
import AutoDeliveryDcxStatusBadge from '@/views/AutoDeliveryDcx/AutoDeliveryDcxStatusBadge';
import {getAutoDeliveryDcxFrequencyLabelFromAppContent} from '@/views/AutoDeliveryDcx/helpers/autoDeliveryDcxFrequency.helpers';

interface AutoDeliveryDcxOrderDetailsProps {
    className?: string;
    status: 'active' | 'onHold';
    orderId?: string | null;
    frequency?: AutoDeliveryFrequency;
    minDeliveryDaysCount: AutoDeliveryBase['minDeliveryDaysCount'];
    maxDeliveryDaysCount: AutoDeliveryBase['maxDeliveryDaysCount'];
    extraSection?: React.ReactNode;
    headingAs?: React.ElementType;
}

const AutoDeliveryDcxOrderDetails = ({
    className,
    status,
    frequency,
    orderId,
    minDeliveryDaysCount,
    maxDeliveryDaysCount,
    extraSection,
    headingAs,
}: AutoDeliveryDcxOrderDetailsProps) => {
    const HeadingComponent = headingAs || 'h1';
    const content = appContent.getAutoDeliveryContent() || {};
    const {extendedDetailsContent} = content;
    const {
        orderDetailsTitle,
        orderDetailsStatusLabel,
        orderDetailsNumberLabel,
        orderDetailsFrequencyLabel,
        orderDetailsDeliveryWithinLabel,
        orderDetailsDeliveryWithinValueLabel,
    } = extendedDetailsContent || {};

    const frequencyLabel = frequency ? getAutoDeliveryDcxFrequencyLabelFromAppContent(frequency) : undefined;

    const deliveryWindow =
        orderDetailsDeliveryWithinLabel &&
        orderDetailsDeliveryWithinValueLabel &&
        minDeliveryDaysCount &&
        maxDeliveryDaysCount
            ? stringUtils.formatString(orderDetailsDeliveryWithinValueLabel, minDeliveryDaysCount, maxDeliveryDaysCount)
            : null;

    const isHeroHeading = headingAs === undefined || headingAs === 'h1';

    return (
        <div className={className}>
            {orderDetailsTitle && (
                <HeadingComponent
                    className={twx(
                        'mb-0 font-bold ml:text-primary',
                        isHeroHeading ? 'mb-4 text-32 leading-[40px] ml:leading-[42px]' : 'text-20 leading-[28px]'
                    )}
                >
                    {orderDetailsTitle}
                </HeadingComponent>
            )}
            <div className='mt-2 flex flex-col gap-2 ml:mt-4'>
                {orderDetailsStatusLabel && (
                    <DetailRow label={orderDetailsStatusLabel} value={<AutoDeliveryDcxStatusBadge status={status} />} />
                )}
                {orderDetailsNumberLabel && orderId && <DetailRow label={orderDetailsNumberLabel} value={orderId} />}
                {orderDetailsFrequencyLabel && frequencyLabel && (
                    <DetailRow label={orderDetailsFrequencyLabel} value={frequencyLabel} />
                )}
                {deliveryWindow && <DetailRow label={orderDetailsDeliveryWithinLabel} value={deliveryWindow} />}
            </div>
            {extraSection}
        </div>
    );
};

export default AutoDeliveryDcxOrderDetails;

interface DetailRowProps {
    label?: string;
    value: React.ReactNode;
}

const DetailRow = ({label, value}: DetailRowProps) => (
    <div className='flex items-center justify-between text-black'>
        <span className='text-16 leading-[24px] tracking-02'>{label}</span>
        <span className='text-16 leading-[24px] tracking-02'>{value}</span>
    </div>
);
